import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * Tag — a small uppercase mono pill used to label state, route, or kind.
 * Shorter and more typographic than Badge — used inside cards and headers.
 */
const tagVariants = cva(
  [
    'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-[4px]',
    'font-geist-mono text-[10px] font-medium leading-none uppercase tracking-[0.06em]',
    'border',
  ],
  {
    variants: {
      variant: {
        muted:   'bg-fd-surface-2 text-fd-text-muted border-fd-border',
        accent:  'bg-fd-accent text-fd-text border-fd-text',
        ok:      'bg-[oklch(0.74_0.14_145/0.14)] text-fd-ok border-[oklch(0.74_0.14_145/0.30)]',
        warn:    'bg-[oklch(0.78_0.13_65/0.14)] text-fd-warn border-[oklch(0.78_0.13_65/0.30)]',
        bad:     'bg-[oklch(0.66_0.21_25/0.14)] text-fd-bad border-[oklch(0.66_0.21_25/0.30)]',
        outline: 'bg-transparent text-fd-text-subtle border-fd-border',
      },
    },
    defaultVariants: { variant: 'muted' },
  },
);

export interface TagProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof tagVariants> {}

export function Tag({ className, variant, ...props }: TagProps) {
  return <span className={cn(tagVariants({ variant }), className)} {...props} />;
}

export { tagVariants };
